import React from "react";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import PremiumDeliveryDetailsCard from "./PremiumDeliveryDetailsCard";
import QuickSelectionTabs from "./QuickSelectionTabs";

interface DeliverySelectionCardProps {
  storeId: any;
  isLoadingDeliveries: boolean;
  isRTL: boolean;

  selectedCity: any;
  storeDeliveryOptions: any[];

  getDeliveryCardColor: (storeId: any) => string;
  t: (key: string) => string;

  onSelectCity: (city: any | null) => void;
  onClearCity: () => void;

  showProviderInfo: boolean;
  onToggleProviderInfo: () => void;

  getRandomProvider: () => any;
  calculateDeliveryDate: (days: number) => string;
}

const DeliverySelectionCard: React.FC<DeliverySelectionCardProps> = ({
  storeId,
  isLoadingDeliveries,
  isRTL,
  selectedCity,
  storeDeliveryOptions,
  getDeliveryCardColor,
  t,
  onSelectCity,
  onClearCity,
  showProviderInfo,
  onToggleProviderInfo,
  getRandomProvider,
  calculateDeliveryDate,
}) => {
      const DeliverySkeletonLoader = () => (
    <div className="animate-pulse space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <div className="h-4 bg-gray-200 rounded w-16"></div>
      </div>
      <div className="h-12 bg-gray-200 rounded-lg"></div>
      <div className="grid grid-cols-2 gap-2">
        <div className="h-20 bg-gray-200 rounded"></div>
        <div className="h-20 bg-gray-200 rounded"></div>
      </div>
    </div>
  );
  return (
    <div className="mt-8">
      {isLoadingDeliveries ? (
        <DeliverySkeletonLoader />
      ) : (
        <div
          className={`bg-gradient-to-r ${getDeliveryCardColor(
            storeId
          )} rounded-2xl p-6 border-2 shadow-lg transition-all duration-500`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between mb-6 ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            <div
              className={`flex items-center gap-4 ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <div className="p-3 bg-gradient-to-r from-white to-gray-50 rounded-xl shadow-md">
                <LocationOnIcon
                  className={`text-2xl ${
                    selectedCity ? "text-emerald-600" : "text-gray-400"
                  }`}
                />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {t("cart.deliveryDestination")}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedCity
                    ? t("cart.destinationSelected")
                    : t("cart.chooseDestination")}
                </p>
              </div>
            </div>

            {selectedCity && (
              <button
                onClick={onClearCity}
                className="px-3 py-2 bg-gradient-to-r from-red-50 to-pink-50 text-red-600
                hover:text-white hover:from-red-600 hover:to-pink-600
                rounded-lg transition-all duration-200 flex items-center gap-1
                text-sm font-medium"
              >
                <ClearIcon fontSize="small" />
                {t("cart.changeCity")}
              </button>
            )}
          </div>

          {/* Select */}
          <div className="relative mb-6">
            <select
              className={`appearance-none w-full px-5 py-4 pr-14 rounded-xl border-2
              transition-all duration-500 focus:outline-none focus:ring-4
              focus:ring-emerald-200 cursor-pointer text-base font-medium shadow-sm
              ${
                selectedCity
                  ? "border-emerald-500 bg-white"
                  : "border-gray-300 hover:border-emerald-400 bg-white"
              }
              ${isRTL ? "text-right" : "text-left"}`}
              value={selectedCity?.city?.id || ""}
              onChange={(e) => {
                const selected =
                  storeDeliveryOptions.find(
                    (opt) => opt.city.id === Number(e.target.value)
                  ) || null;
                onSelectCity(selected);
                if (selected) {
                  setTimeout(onToggleProviderInfo, 300);
                }
              }}
            >
              <option value="">{t("cart.selectDestination")}</option>
              {storeDeliveryOptions.map((opt) => (
                <option key={opt.city.id} value={opt.city.id}>
                  {opt.city.name} – {opt.delivery_fee} {t("currency.egp")}
                </option>
              ))}
            </select>

            <div
              className={`pointer-events-none absolute inset-y-0 flex items-center ${
                isRTL ? "left-5" : "right-5"
              }`}
            >
              <ArrowForwardIcon
                className={`text-xl transition-all duration-500 rotate-90 ${
                  selectedCity ? "text-emerald-600" : "text-gray-400"
                }`}
              />
            </div>
          </div>

          {/* Details */}
          {selectedCity && (
            <PremiumDeliveryDetailsCard
              selectedCity={selectedCity}
              providerInfo={getRandomProvider()}
              showProviderInfo={showProviderInfo}
              isRTL={isRTL}
              t={t}
              currencySymbol={t("currency.egp")}
              onToggleProviderInfo={onToggleProviderInfo}
              // calculateDeliveryDate={calculateDeliveryDate}
            />
          )}

          {/* Quick Tabs */}
          {!selectedCity && storeDeliveryOptions.length > 0 && (
            <QuickSelectionTabs
              storeId={storeId}
              cityOptions={storeDeliveryOptions}
              selectedCity={selectedCity}
              isRTL={isRTL}
              t={t}
              currencySymbol={t("currency.egp")}
              onSelectCity={(opt) => {
                onSelectCity(opt);
                setTimeout(onToggleProviderInfo, 300);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DeliverySelectionCard;
